/**
 * eventTime — one instant interpretation for match-event timestamps.
 *
 * Two different timestamp shapes reach the client for the same logical event:
 *
 *  - Optimistic (client-authored) events carry `new Date().toISOString()`,
 *    which always ends in `Z`.
 *  - Server-acked / refetched events carry whatever Mongo gave back. The
 *    backend's Motor client is created without `tz_aware=True`, so datetimes
 *    read out of Mongo are NAIVE UTC and serialize with no timezone suffix.
 *
 * `new Date("2026-08-22T14:40:23.123")` parses a naive string as LOCAL time.
 * In a negative-offset zone (UTC-6 for this product's operators) that places
 * every server-acked event HOURS after every optimistic one, so any sort that
 * mixes the two shapes silently reorders the log.
 *
 * That is not hypothetical: it is why the cockpit's "End T.Off" button looked
 * dead. `computeIneffectiveBreakdown` replayed an optimistic `TimeoutStop`
 * BEFORE the acked `TimeoutStart` it was meant to close, so the stop was a
 * no-op against no open timeout and the start then re-opened one —
 * `timeout.active` never cleared.
 *
 * Every timestamp comparison on match events must go through this module.
 */

/** Epoch ms for an event timestamp, reading a timezone-less ISO string as UTC. */
export const parseEventTimestampMs = (ts?: string | null): number => {
  if (!ts) return NaN;
  const s = String(ts).trim();
  // Already carries timezone info ("Z" or "+HH:MM" / "-HH:MM").
  if (/[Zz]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s).getTime();
  }
  // ISO-shaped but timezone-less → treat as UTC, matching the backend.
  if (s.includes("T")) {
    return new Date(s + "Z").getTime();
  }
  return new Date(s).getTime();
};

/**
 * Ascending chronological comparator. Unparseable timestamps sort as equal so
 * the caller's own stable tie-break (usually array order) decides, rather than
 * NaN poisoning the comparison.
 */
export const compareEventTimestamps = (
  left?: string | null,
  right?: string | null,
): number => {
  const leftMs = parseEventTimestampMs(left);
  const rightMs = parseEventTimestampMs(right);
  if (!Number.isFinite(leftMs) || !Number.isFinite(rightMs)) return 0;
  if (leftMs === rightMs) return 0;
  return leftMs - rightMs;
};
